import React, { useCallback, useEffect, useState } from 'react';
import { Keyboard, TouchableOpacity, View } from 'react-native';
import { useNavigation, useNavigationState } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';

import { ROLE_PERMISSIONS } from '../../../common/constants';
import { useUserContext } from '../../../common/utils/context';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import useMyDayMode, { MY_DAY_TICKET_SCOPE } from '../../scenes/myDay/useMyDayMode';
import { fontSizes, useTheme } from '../../theme';
import Text from '../text/Text';
import { setFieldTabBarHeight } from './fieldTabBarHeight';

// The bar only shows on the hub-level screens; pushed detail and data-entry
// screens keep their full height (e.g. pick list with the tub weight dock).
const TAB_BAR_ROUTES = new Set(['Home', 'MyDayView', 'ServiceRunListView', 'TicketListView']);

/**
 * Bottom tab bar for field staff (My day users): one-tap switching between
 * the My day board, the classic service run list, and tickets.
 */
const FieldTabBar = () => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const { theme } = useTheme();
	const insets = useSafeAreaInsets();
	const { user } = useUserContext();
	const { canAccessMyDayFeature, ticketScope } = useMyDayMode();
	const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

	// This component renders inside the drawer's MainStack screen, so the
	// navigation state here is the drawer's; dig out the active stack route.
	const currentRouteName = useNavigationState((state) => {
		const mainStackRoute = state?.routes?.find(route => route.name === 'MainStack');
		const stackState = mainStackRoute?.state;
		if (!stackState?.routes?.length) {
			return 'Home';
		}

		return stackState.routes[stackState.index]?.name || 'Home';
	});

	useEffect(() => {
		const showSubscription = Keyboard.addListener('keyboardDidShow', () => setIsKeyboardOpen(true));
		const hideSubscription = Keyboard.addListener('keyboardDidHide', () => setIsKeyboardOpen(false));

		return () => {
			showSubscription.remove();
			hideSubscription.remove();
		};
	}, []);

	const goToScreen = useCallback(screenName => () => {
		navigation.navigate('MainStack', { screen: screenName });
	}, [navigation]);
	const onBarLayout = useCallback((event) => {
		setFieldTabBarHeight(event.nativeEvent.layout.height);
	}, []);

	const isVisible = canAccessMyDayFeature && !isKeyboardOpen && TAB_BAR_ROUTES.has(currentRouteName);

	// Root-level overlays (sync banner) offset themselves by the published
	// height; reset it whenever the bar hides or unmounts.
	useEffect(() => {
		if (!isVisible) {
			setFieldTabBarHeight(0);
		}
	}, [isVisible]);
	useEffect(() => () => setFieldTabBarHeight(0), []);

	if (!isVisible) {
		return null;
	}

	const canViewServiceRuns = Boolean(user && (user.isSuperAdmin || user.role.permissions.includes(ROLE_PERMISSIONS.VIEW_SERVICE_RUN_LIST_PAGE)));
	const canViewTicketList = ticketScope === MY_DAY_TICKET_SCOPE.DRIVER || Boolean(user && (
		user.isSuperAdmin || user.role.permissions.includes(ROLE_PERMISSIONS.VIEW_TICKET_LIST_PAGE)
	));

	// Active tabs use the filled icon variant.
	const tabs = [
		{
			key: 'myDay',
			label: 'My day',
			icon: 'sunny-outline',
			activeIcon: 'sunny',
			onPress: goToScreen('Home'),
			active: currentRouteName === 'Home' || currentRouteName === 'MyDayView',
			visible: true,
		},
		{
			key: 'serviceRuns',
			label: 'Runs',
			icon: 'car-outline',
			activeIcon: 'car',
			onPress: goToScreen('ServiceRunListView'),
			active: currentRouteName === 'ServiceRunListView',
			visible: canViewServiceRuns,
		},
		{
			key: 'tickets',
			label: 'Tickets',
			icon: 'ticket-outline',
			activeIcon: 'ticket',
			onPress: goToScreen('TicketListView'),
			active: currentRouteName === 'TicketListView',
			visible: canViewTicketList,
		},
	].filter(tab => tab.visible);

	return (
		<View style={[styles.container, { paddingBottom: Math.max(insets.bottom, 6) }]} onLayout={onBarLayout}>
			{
				tabs.map(tab => (
					<TouchableOpacity
						key={tab.key}
						style={styles.tab}
						activeOpacity={0.7}
						onPress={tab.onPress}
					>
						<Ionicons
							name={tab.active ? (tab.activeIcon || tab.icon) : tab.icon}
							size={22}
							color={tab.active ? theme.primary1 : theme.text2}
						/>
						<Text style={[styles.tabLabel, tab.active && styles.tabLabelActive]}>{tab.label}</Text>
					</TouchableOpacity>
				))
			}
		</View>
	);
};

const createStyles = (theme) => ({
	container: {
		flexDirection: 'row',
		backgroundColor: theme.mainBg1,
		borderTopWidth: 1,
		borderTopColor: theme.border3,
		paddingTop: 6,
	},
	tab: {
		flex: 1,
		alignItems: 'center',
		paddingVertical: 4,
	},
	tabLabel: {
		marginTop: 2,
		fontSize: fontSizes.xs,
		color: theme.text2,
	},
	tabLabelActive: {
		color: theme.primary1,
		fontWeight: '600',
	},
});

export default FieldTabBar;
